import CancelIcon from '@material-design-icons/svg/filled/cancel.svg?react';
import CheckCircleIcon from '@material-design-icons/svg/filled/check_circle.svg?react';
import DoDisturbOffIcon from '@material-design-icons/svg/filled/do_disturb_off.svg?react';
import ErrorIcon from '@material-design-icons/svg/filled/error.svg?react';
import FiberManualRecordIcon from '@material-design-icons/svg/filled/fiber_manual_record.svg?react';
import HighlightOffIcon from '@material-design-icons/svg/filled/highlight_off.svg?react';
import RemoveRedEyeIcon from '@material-design-icons/svg/filled/remove_red_eye.svg?react';
import ScienceIcon from '@material-design-icons/svg/filled/science.svg?react';
import VisibilityOffIcon from '@material-design-icons/svg/filled/visibility_off.svg?react';
import WarningIcon from '@material-design-icons/svg/filled/warning.svg?react';
import { ComponentType } from 'react';
import {
  LiveVersionComparison,
  ReleaseStatus,
  SubmissionStatus,
} from '../types/api';

export type { ReleaseStatus, SubmissionStatus };

export type CombinedStatus =
  | 'InReview'
  | 'Live'
  | 'Deprecated'
  | 'Suspended'
  | 'Rejected'
  | 'Processing'
  | 'Processed'
  | 'ProcessingFailed'
  | 'AlphaTesting'
  | 'ActionNeeded'
  | 'Error';

interface StatusConfig {
  label: string;
  icon: ComponentType<{ className?: string }>;
  iconClassName: string;
  textColorClassName: string;
}

const combinedStatusConfig: Record<CombinedStatus, StatusConfig> = {
  InReview: {
    label: 'In Review',
    icon: RemoveRedEyeIcon,
    iconClassName: 'w-4 h-4 fill-yellow-400',
    textColorClassName: 'text-yellow-400',
  },
  Live: {
    label: 'Live',
    icon: FiberManualRecordIcon,
    iconClassName: 'w-4 h-4 fill-green-500 animate-live-pulse',
    textColorClassName: 'text-green-500',
  },
  Deprecated: {
    label: 'Deprecated',
    icon: VisibilityOffIcon,
    iconClassName: 'w-4 h-4 fill-neutral-500',
    textColorClassName: 'text-neutral-500',
  },
  Suspended: {
    label: 'Suspended',
    icon: ErrorIcon,
    iconClassName: 'w-4 h-4 fill-orange-500',
    textColorClassName: 'text-orange-500',
  },
  Rejected: {
    label: 'Rejected',
    icon: HighlightOffIcon,
    iconClassName: 'w-4 h-4 fill-red-500',
    textColorClassName: 'text-red-500',
  },
  Processing: {
    label: 'Processing',
    icon: DoDisturbOffIcon,
    iconClassName: 'w-4 h-4 fill-orange-500',
    textColorClassName: 'text-orange-500',
  },
  Processed: {
    label: 'Processed',
    icon: CheckCircleIcon,
    iconClassName: 'w-4 h-4 fill-white',
    textColorClassName: 'text-white',
  },
  ProcessingFailed: {
    label: 'Processing Failed',
    icon: CancelIcon,
    iconClassName: 'w-4 h-4 fill-red-500',
    textColorClassName: 'text-red-500',
  },
  AlphaTesting: {
    label: 'Alpha Testing',
    icon: ScienceIcon,
    iconClassName: 'w-4 h-4 fill-purple-500',
    textColorClassName: 'text-purple-500',
  },
  ActionNeeded: {
    label: 'Action Needed',
    icon: WarningIcon,
    iconClassName: 'w-4 h-4 fill-amber-500',
    textColorClassName: 'text-amber-500',
  },
  Error: {
    label: 'Error',
    icon: ErrorIcon,
    iconClassName: 'w-4 h-4 fill-red-600',
    textColorClassName: 'text-red-600',
  },
};

export interface ReleaseStatusOverrides {
  isLive?: boolean;
  liveVersionComparison?: LiveVersionComparison;
  hubspotTicketId?: string | null;
  hubspotTicketClosedDate?: string | null;
  nftMintAddress?: string | null;
  isSupersededByNewerSubmission?: boolean;
}

function resolveCombinedStatus(
  releaseStatus: ReleaseStatus,
  submissionStatus: SubmissionStatus,
  overrides: ReleaseStatusOverrides,
): CombinedStatus {
  const {
    isLive,
    liveVersionComparison,
    hubspotTicketId,
    hubspotTicketClosedDate,
    nftMintAddress,
    isSupersededByNewerSubmission,
  } = overrides;
  const isHubspotTicketOpen =
    Boolean(hubspotTicketId) && hubspotTicketClosedDate == null;
  const isHubspotTicketClosed =
    Boolean(hubspotTicketId) && hubspotTicketClosedDate != null;

  if (isLive === true) {
    return 'Live';
  }

  if (isSupersededByNewerSubmission) {
    return 'Deprecated';
  }

  if (liveVersionComparison === 'Older') {
    return 'Deprecated';
  }

  if (
    releaseStatus === 'Processed' &&
    submissionStatus === 'NotSubmitted' &&
    (!nftMintAddress || !hubspotTicketId)
  ) {
    return 'ActionNeeded';
  }

  if (releaseStatus === 'Published') {
    return 'Live';
  }

  if (submissionStatus === 'AlphaTest') {
    return 'AlphaTesting';
  }

  if (submissionStatus === 'Submitted' || submissionStatus === 'UnderReview') {
    return releaseStatus === 'Processed' && isHubspotTicketClosed
      ? 'Processed'
      : 'InReview';
  }

  if (submissionStatus === 'Approved') {
    return isHubspotTicketOpen ? 'InReview' : 'Processed';
  }

  if (submissionStatus === 'ProcessingFailed') {
    return 'Error';
  }

  if (submissionStatus === 'RequiresChanges') {
    return 'Suspended';
  }

  if (submissionStatus === 'Rejected' || releaseStatus === 'Rejected') {
    return 'Rejected';
  }

  if (releaseStatus === 'Archived') {
    return 'Deprecated';
  }

  if (releaseStatus === 'Processing') {
    return 'Processing';
  }

  if (releaseStatus === 'ProcessingFailed') {
    return 'ProcessingFailed';
  }

  return 'Processed';
}

export function getStatusDisplay(
  releaseStatus: ReleaseStatus,
  submissionStatus: SubmissionStatus,
  overrides: ReleaseStatusOverrides = {},
): StatusConfig {
  const combinedStatus = resolveCombinedStatus(
    releaseStatus,
    submissionStatus,
    overrides,
  );
  return combinedStatusConfig[combinedStatus];
}
